import biometricService from '../shared/BiometricService';
import { toast } from 'react-toastify';
import { requester } from '../App';
import { devLog } from "utils/logger";
// 고정 콜백 이름 상수
const FIXED_CALLBACK_NAME = 'pinVerificationCallback';

class PinVerificationManager {
  constructor() {
    this.verificationAttempts = 0;
    this.maxAttempts = 3;
    this.isVerifying = false;
    this.isCallbackResolved = false;
    this.newVerificationStarted = false;
    this.currentCallbackName = null;

    // 🔥 사용자 입력 상태 추적을 위한 변수 추가
    this.isUserInputting = false;
    this.inputStartTime = null;
    this.isCallbackResolved = false; // ⭐ 콜백 해결 상태를 클래스 멤버로 관리
    this.newVerificationStarted = false; // 새로운 검증 시작 신호 추가
  }

  /**
   * 서버 PIN 검증
   * @param {string} pinNumber - 검증할 PIN 번호
   * @param {Object} userData - 사용자 데이터
   * @returns {Promise<boolean>} 검증 성공 여부
   */
  async verifyPinWithServer(pinNumber, userData) {
    try {
      devLog("[verifyPinWithServer] 🔥🔥🔥 서버 PIN 검증 시작 🔥🔥🔥", userData);

      const requestData = {
        userName: userData.phoneNumber,
        pinNumber: pinNumber,
        deviceId: biometricService.getDeviceIdentifier ? biometricService.getDeviceIdentifier() : 'unknown'
      };

      devLog("[verifyPinWithServer] 서버 PIN 검증 요청 데이터:", requestData);

      return new Promise((resolve, reject) => {
        // 타임아웃 설정 (10초)
        const timeoutId = setTimeout(() => {
          devLog("[verifyPinWithServer] 🔥 서버 응답 타임아웃 (10초)");
          resolve(false);
        }, 10000);

        try {
          // requester를 사용하여 서버에 검증 요청
          requester.verifyPin(requestData, (result) => {
            clearTimeout(timeoutId); // 타임아웃 해제

            devLog("[verifyPinWithServer] 🔥🔥🔥 서버 PIN 검증 응답 🔥🔥🔥:", result);
            devLog("[verifyPinWithServer] 🔥 result.code:", result.code);
            devLog("[verifyPinWithServer] 🔥 result.data:", result.data);
            devLog("[verifyPinWithServer] 🔥 result.data === true:", result.data === true);

            if (result.code === 0 && result.data === true) {
              devLog("[verifyPinWithServer] 🔥 서버 PIN 검증 성공 - true 반환");
              resolve(true);
            } else {
              devLog("[verifyPinWithServer] 🔥 서버 PIN 검증 실패 - false 반환");
              devLog("[verifyPinWithServer] 🔥 실패 이유: code =", result.code, ", data =", result.data);
              resolve(false);
            }
          });
        } catch (requesterError) {
          clearTimeout(timeoutId);
          console.error("[verifyPinWithServer] 🔥 requester 호출 오류:", requesterError);
          resolve(false);
        }
      });
    } catch (error) {
      console.error("[verifyPinWithServer] 🔥 서버 PIN 검증 오류:", error);
      return false; // 오류 시 실패로 처리
    }
  }

  /**
   * PIN 검증 프로세스 실행
   * @param {Object} formData - 폼 데이터
   * @param {Object} callbacks - 콜백 함수들
   * @param {string} mode - 호출 모드 ('auth' 또는 'main')
   */
  async executeVerification(formData, callbacks, mode = 'main') {
    devLog(`[PIN 검증] 🔄 executeVerification 시작 - 시도 횟수: ${this.verificationAttempts}, 모드: ${mode}`);

    // 🔥 모드 저장
    this.currentMode = mode;

    // 🔥 상태 완전 초기화 (콜백 중복 처리 방지)
    this.isCallbackResolved = false;
    this.newVerificationStarted = true;
    devLog("[PIN 검증] 🔍 상태 초기화 완료: isCallbackResolved=false, newVerificationStarted=true");

    // 🔥 사용자 입력 상태 초기화
    this._resetUserInputState();

    // 기존 콜백 정리
    this._cleanupCallback();
    devLog("[PIN 검증] 기존 콜백 함수 삭제:", this.currentCallbackName);

    // 기존 콜백 함수 완전 삭제
    if (window[FIXED_CALLBACK_NAME]) {
      delete window[FIXED_CALLBACK_NAME];
      devLog(`[PIN 검증] 기존 콜백 함수 삭제: ${FIXED_CALLBACK_NAME}`);
    }

    // 🔥 재시도 시에도 기존 프래그먼트를 reset하여 재사용 (일관성 확보)
    if (this.verificationAttempts > 0) {
      devLog(`[PIN 검증] 🔄 재시도 감지 - 현재 시도 횟수: ${this.verificationAttempts}`);
      devLog(`[PIN 검증] 🔄 기존 프래그먼트 reset 후 재사용 시작`);
      this._updatePinFragmentState('reset');
      devLog("[PIN 검증] 🔄 reset 호출 완료, 10ms 대기 시작");

      // reset이 완료될 때까지 잠시 대기 (시간 더 단축)
      await new Promise(resolve => setTimeout(resolve, 10)); // 🔥 30ms → 10ms로 단축 (속도 개선)
      devLog("[PIN 검증] 🔄 reset 대기 완료, 다음 단계 진행");
    } else {
      devLog(`[PIN 검증] 🔄 첫 번째 시도 - reset 건너뛰기`);
    }

    // 🔥 네이티브 프래그먼트 상태도 초기화
    this._resetNativePinFragmentState();

    // 중복 검증 방지
    if (this.isVerifying) {
      devLog("[PIN 검증] ⚠️ 이미 검증 중이므로 무시");
      return;
    }

    try {
      devLog("[PIN 검증] ✅ 검증 시작 - isVerifying을 true로 설정");
      this.isVerifying = true;
      callbacks.onStateChange('verifying');

      // 저장된 정보 확인
      const hasStoredInfo = await this._hasStoredPinInfo();
      devLog("[PIN 검증] 🔍 저장된 정보 확인 상세:", {
        hasStoredInfo,
        시도횟수: this.verificationAttempts,
        콜백상태: this.isCallbackResolved
      });

      if (hasStoredInfo) {
        devLog("[PIN 검증] 🔄 저장된 정보 검증 경로 - _handleStoredPinVerification 호출");
        await this._handleStoredPinVerification(formData, callbacks);
      } else {
        devLog("[PIN 검증] 🔄 네이티브 PIN 다이얼로그 경로 - _handleNativePinDialog 호출");
        devLog("[PIN 검증] 🔍 다이얼로그 호출 전 콜백 상태:", this.isCallbackResolved);
        await this._handleNativePinDialog(formData, callbacks);
        devLog("[PIN 검증] 🔍 다이얼로그 호출 후 콜백 상태:", this.isCallbackResolved);
      }
    } catch (error) {
      console.error("[PIN 검증] 오류 발생:", error);
      callbacks.onError(error);
    } finally {
      devLog("[PIN 검증] executeVerification 종료, isVerifying을 false로 설정");
      this.isVerifying = false;
      // 🔥 무조건 idle로 변경하지 않음 - 각 콜백에서 적절한 상태로 설정
      // callbacks.onStateChange('idle'); // 제거

      // 콜백 함수는 재시도 가능한 경우에만 유지
      if (this.verificationAttempts < this.maxAttempts) {
        devLog(`[PIN 검증] 콜백 함수 유지 (재시도 가능): ${FIXED_CALLBACK_NAME}`);
      }
      devLog(`[PIN 검증] 🔍 finally 블록에서 콜백 상태: ${this.isCallbackResolved}`);
    }
  }

  /**
   * 콜백 함수 정리 (성공 또는 최대 시도 횟수 도달 시)
   */
  _cleanupCallback() {
    if (window[FIXED_CALLBACK_NAME]) {
      devLog(`[PIN 검증] 콜백 함수 정리: ${FIXED_CALLBACK_NAME}`);
      delete window[FIXED_CALLBACK_NAME];
    }
  }

  /**
   * 저장된 PIN으로 검증 처리
   */
  async _handleStoredPinVerification(formData, callbacks) {
    const { onSuccess, onFailure, onError, onStateChange } = callbacks;

    try {
      // 🔥 재시도인 경우 생체인증 건너뛰기
      const isRetry = this.verificationAttempts > 0;
      devLog(`[PIN 검증] 재시도 여부: ${isRetry}, 현재 시도 횟수: ${this.verificationAttempts}`);

      if (!isRetry) {
        // 🔥 1단계: 생체인증 지원 여부 확인 및 우선 시도 (첫 시도에만)
        const biometricSupported = biometricService.isSupported();
        let biometricKeyExists = false;

        if (biometricSupported) {
          try {
            biometricKeyExists = await biometricService.hasRegisteredKey();
            devLog("[PIN 검증] 생체인증 키 존재 여부:", biometricKeyExists);
          } catch (error) {
            console.error("[PIN 검증] 생체인증 키 확인 오류:", error);
            biometricKeyExists = false;
          }
        }

        // 🔥 2단계: 생체인증 우선 시도 (키가 있는 경우)
        if (biometricSupported && biometricKeyExists) {
          devLog("[PIN 검증] 🔐 생체인증 우선 시도 시작 (첫 시도)");
          onStateChange?.('verifying', '생체인증 시도 중...');

          try {
            // 간단한 챌린지 데이터 생성
            const challengeData = `pin_verification_${Date.now()}`;

            // 생체인증 시도
            const biometricResult = await biometricService.authenticateAndSignData(
              formData.phoneNumber,
              challengeData
            );

            devLog("[PIN 검증] 🔐 생체인증 성공, 서버 검증 진행");
            onStateChange?.('server_verifying', '생체인증 서버 검증 중...');

            // 서버에 생체인증 검증 요청 (필요시 구현)
            // 현재는 생체인증 성공 시 바로 로그인 진행
            devLog("[PIN 검증] 🔐 생체인증 검증 성공 - 로그인 진행");

            // 디바이스 정보 저장
            try {
              biometricService.savePhoneNumber(formData.phoneNumber);
              devLog("[PIN 검증] 전화번호 디바이스에 저장 완료");
            } catch (storageError) {
              console.error("[PIN 검증] 디바이스 저장 실패:", storageError);
            }

            // 성공 시 콜백 정리
            this._cleanupCallback();
            onSuccess?.();
            return; // 생체인증 성공 시 PIN 검증 건너뛰기

          } catch (biometricError) {
            devLog("[PIN 검증] 🔐 생체인증 실패/취소 - PIN 입력으로 전환:", biometricError.message);
            // 생체인증 실패 시 PIN 입력으로 계속 진행
          }
        } else {
          devLog("[PIN 검증] 🔐 생체인증 불가 - PIN 입력으로 진행");
        }
      } else {
        devLog("[PIN 검증] 🔄 재시도 - 생체인증 건너뛰고 바로 PIN 입력");
      }

      // 🔥 3단계: PIN 입력 검증 (생체인증 실패/취소 시 또는 생체인증 불가 시 또는 재시도 시)
      devLog("[PIN 검증] 📱 PIN 입력 검증 시작");
      onStateChange?.('verifying', 'PIN 입력 대기 중...');

      // 🔥 BiometricService 대신 우리가 만든 콜백 시스템 사용
      devLog("[PIN 검증] 🔥🔥🔥 네이티브 PIN 다이얼로그 콜백 등록 시작 🔥🔥🔥");
      this._setupNativePinDialogCallback(formData, callbacks);

      // 네이티브 다이얼로그 호출
      devLog("[PIN 검증] 🔥🔥🔥 네이티브 PIN 다이얼로그 호출 🔥🔥🔥");
      this._callNativePinDialog();
    } catch (error) {
      console.error("[PIN 검증] 저장된 PIN 검증 오류:", error);
      onError?.(error);
    }
  }

  /**
   * 네이티브 PIN 다이얼로그 처리
   */
  async _handleNativePinDialog(formData, callbacks) {
    const { onSuccess, onFailure, onError, onStateChange } = callbacks;

    devLog("[PIN 검증] _handleNativePinDialog 시작");
    devLog("[PIN 검증] formData:", {
      phoneNumber: formData.phoneNumber,
      hasPinNumber: !!formData.pinNumber,
      pinLength: formData.pinNumber ? formData.pinNumber.length : 0
    });

    try {
      // 🔥 첫 시도인 경우 Face ID 먼저 시도 (mode에 따라 다른 처리)
      if (this.verificationAttempts === 0) {
        devLog("[PIN 검증] 🔐 첫 시도 - Face ID 우선 시도");
        
        // 🔥 mode에 따라 다른 Face ID 처리
        if (this.currentMode === 'main') {
          // Main 모드: mainFaceId 처리
          await this._handleMainFaceId(formData, callbacks);
          return;
        } else {
          // Auth 모드: 기존 authFaceId 처리
          this._setupFaceIdCallback(formData, callbacks);
          
          devLog("[PIN 검증] 🔐 authFaceId 명령어 호출");
          if (window.webkit?.messageHandlers?.BRIDGE) {
            window.webkit.messageHandlers.BRIDGE.postMessage("authFaceId");
            devLog("[PIN 검증] ✅ authFaceId 명령어 전송 완료");
            return; // 여기서 멈춤 (콜백에서 계속 진행)
          } else {
            console.warn("[PIN 검증] iOS 브릿지 없음 - PIN 다이얼로그로 폴백");
            // iOS 브릿지가 없으면 바로 PIN 다이얼로그로
          }
        }
      } else {
        devLog("[PIN 검증] 🔄 재시도 - 바로 PIN 다이얼로그");
      }

      // formData.pinNumber가 있는 경우 서버 검증 진행
      if (formData.pinNumber && formData.pinNumber.length === 6) {
        devLog("[PIN 검증] ⭐ 경로 A: 이미 입력된 PIN 번호가 있음, 서버 검증 진행");
        devLog("[PIN 검증] PIN 값:", formData.pinNumber);

        onStateChange?.('server_verifying');

        devLog("[PIN 검증] verifyPinWithServer 호출 직전");
        const serverVerificationSuccess = await this.verifyPinWithServer(formData.pinNumber, formData);
        devLog("[PIN 검증] verifyPinWithServer 호출 완료, 결과:", serverVerificationSuccess);

        if (serverVerificationSuccess) {
          devLog("[PIN 검증] 서버 PIN 검증 성공 - 로그인 진행");

          // 네이티브에 서버 검증 성공 알림
          this._notifyPinVerificationSuccess(formData.pinNumber);

          // 디바이스에 정보 저장
          await this._saveUserDataToDevice(formData, formData.pinNumber);

          // 성공 시 콜백 정리
          this._cleanupCallback();
          onSuccess?.();
        } else {
          devLog("[PIN 검증] ⭐ 서버 PIN 검증 실패 - _handleVerificationFailure 호출");
          devLog("[PIN 검증] 🔥🔥🔥 _handleVerificationFailure 호출 직전 🔥🔥🔥");
          devLog("[PIN 검증] 🔥 callbacks 객체 확인:", callbacks);
          devLog("[PIN 검증] 🔥 callbacks.onStateChange 존재:", !!callbacks.onStateChange);

          // 6번 방식으로 실패 처리
          await this._handleVerificationFailure(callbacks);

          devLog("[PIN 검증] 🔥🔥🔥 _handleVerificationFailure 호출 완료 🔥🔥🔥");
        }
      } else {
        devLog("[PIN 검증] ⭐ 경로 B: PIN 다이얼로그 표시 필요");
        devLog("[PIN 검증] formData.pinNumber 상태:", formData.pinNumber);

        // 🔥 Promise/await 방식 대신 콜백 기반 라이프사이클 관리
        devLog("[PIN 검증] 🔥🔥🔥 네이티브 PIN 다이얼로그 콜백 등록 시작 🔥🔥🔥");
        this._setupNativePinDialogCallback(formData, callbacks);

        // 네이티브 다이얼로그 호출
        devLog("[PIN 검증] 🔥🔥🔥 네이티브 PIN 다이얼로그 호출 🔥🔥🔥");
        this._callNativePinDialog();
      }
    } catch (error) {
      console.error("[PIN 검증] _handleNativePinDialog 오류:", error);
      onError?.(error);
    }
  }

  /**
   * 🔐 Face ID 콜백 등록 및 처리
   */
  _setupFaceIdCallback(formData, callbacks) {
    const { onSuccess, onFailure, onError, onStateChange } = callbacks;
    
    devLog("[PIN 검증] 🔐 Face ID 콜백 등록 시작");
    
    // Face ID 콜백 함수 등록
    window.BRIDGE.authFaceIdCallback = (result) => {
      devLog("[PIN 검증] 🔐 Face ID 콜백 받음:", result);
      
      // 🔥 "PIN" 값은 PIN 입력 모드로의 전환을 의미함 (Face ID 성공이 아님)
      if (result === 'PIN') {
        devLog("[PIN 검증] 🔐 네이티브에서 PIN 입력 모드로 전환됨");
        devLog("[PIN 검증] 🔐 PIN 다이얼로그 콜백 등록");
        this._setupNativePinDialogCallback(formData, callbacks);
        return;
      }
      
      if (result === 'cancel') {
        // ❌ Face ID 취소 → 네이티브에서 자동으로 PIN 다이얼로그 호출됨
        devLog("[PIN 검증] 🔐 Face ID 취소 - 네이티브에서 PIN 다이얼로그 자동 호출됨");
        devLog("[PIN 검증] 🔐 PIN 다이얼로그 콜백 등록");
        this._setupNativePinDialogCallback(formData, callbacks);
      } else if (result === 'failure') {
        // ❌ Face ID 실패 → PIN 다이얼로그로 폴백
        devLog("[PIN 검증] 🔐 Face ID 실패 - PIN 다이얼로그로 폴백");
        this._callNativePinDialog();
        this._setupNativePinDialogCallback(formData, callbacks);
      } else if (result && typeof result === 'string' && result.length > 10) {
        // ✅ Face ID 성공 → 생체인증 서버 검증 (실제 생체인증 데이터인 경우만)
        devLog("[PIN 검증] 🔐 Face ID 성공 - 생체인증 서버 검증 시작");
        this._verifyBiometricSignature(result, formData, callbacks);
      } else {
        // ❌ 기타 예상하지 못한 값 → PIN 다이얼로그로 폴백
        devLog("[PIN 검증] 🔐 예상하지 못한 Face ID 결과 - PIN 다이얼로그로 폴백:", result);
        this._callNativePinDialog();
        this._setupNativePinDialogCallback(formData, callbacks);
      }
    };
    
    devLog("[PIN 검증] 🔐 Face ID 콜백 등록 완료");
  }

  /**
   * 🔐 생체인증 서버 검증
   */
  async _verifyBiometricSignature(hexString, formData, callbacks) {
    const { onSuccess, onFailure, onError, onStateChange } = callbacks;
    
    try {
      devLog("[PIN 검증] 🔐 생체인증 서버 검증 시작");
      devLog("[PIN 검증] 🔐 hexString:", hexString.substring(0, 20) + "...");
      
      onStateChange?.('server_verifying', '생체인증 서버 검증 중...');
      
      // TODO: verifyBiometricSignature API 호출 구현
      const success = await this._callBiometricAPI(hexString, formData);
      
      if (success) {
        devLog("[PIN 검증] 🔐 생체인증 서버 검증 성공 - 로그인 진행");
        
        // 디바이스에 정보 저장
        await this._saveUserDataToDevice(formData);
        
        // 성공 시 콜백 정리
        this._cleanupCallback();
        onSuccess?.();
      } else {
        devLog("[PIN 검증] 🔐 생체인증 서버 검증 실패 - PIN 다이얼로그로 폴백");
        
        // 생체인증 실패 → PIN 다이얼로그로 폴백
        this._callNativePinDialog();
        this._setupNativePinDialogCallback(formData, callbacks);
      }
    } catch (error) {
      console.error("[PIN 검증] 🔐 생체인증 서버 검증 오류:", error);
      
      // 오류 시에도 PIN으로 폴백
      devLog("[PIN 검증] 🔐 오류로 인한 PIN 다이얼로그 폴백");
      this._callNativePinDialog();
      this._setupNativePinDialogCallback(formData, callbacks);
    }
  }

  /**
   * 🔐 생체인증 API 호출 (verifyBiometricSignature)
   */
  async _callBiometricAPI(hexString, formData) {
    return new Promise((resolve, reject) => {
      try {
        const verifyData = {
          userName: formData.phoneNumber,
          biometricSignature: hexString,
          // TODO: 필요한 추가 데이터 구성
        };
        
        devLog("[PIN 검증] 🔐 verifyBiometricSignature API 호출");
        devLog("[PIN 검증] 🔐 요청 데이터:", {
          userName: verifyData.userName,
          signatureLength: hexString.length
        });
        
        // TODO: 실제 API 호출 구현 필요
        requester.verifyBiometricSignature(verifyData, (result) => {
          if (result.data) {
            // devLog(result.data);
            resolve(true);
          } else {
            resolve(false);
          }
        });
        
        // 임시로 성공 처리 (실제 API 구현 후 제거)
        // devLog("[PIN 검증] 🔐 임시: 생체인증 API 성공으로 처리");
        // setTimeout(() => resolve(true), 100); // 🔥 500ms → 100ms로 단축 (속도 개선)
        
      } catch (error) {
        console.error("[PIN 검증] 🔐 생체인증 API 호출 오류:", error);
        reject(error);
      }
    });
  }

  /**
   * 네이티브 PIN 다이얼로그 콜백 설정 (라이프사이클 관리)
   * @private
   */
  _setupNativePinDialogCallback(formData, callbacks) {
    const { onSuccess, onFailure, onError, onStateChange } = callbacks;
    const platform = biometricService.getPlatform();

    if (platform === 'android') {
      const callbackName = FIXED_CALLBACK_NAME;

      // 🔥 기존 콜백 함수 완전 삭제 (재시도 시)
      if (window[callbackName]) {
        devLog(`[PIN 검증] 기존 콜백 함수 삭제: ${callbackName}`);
        delete window[callbackName];
      }

      // this 참조를 로컬 변수로 저장 (클로저 문제 해결)
      const self = this;
      devLog("[PIN 다이얼로그] 🔍 self 참조 생성, self.isCallbackResolved:", self.isCallbackResolved);

      // 🔥 새로운 콜백 함수 등록 - 라이프사이클 관리 방식
      window[callbackName] = async (success, errorMessage, pinValue) => {
        devLog(`[PIN 다이얼로그] 🔥🔥🔥 콜백 호출됨: 성공=${success}, 오류=${errorMessage}, PIN값=${pinValue} 🔥🔥🔥`);
        devLog(`[PIN 다이얼로그] 🔍 디버깅 - success 타입: ${typeof success}, 값: ${success}`);
        devLog(`[PIN 다이얼로그] 🔍 디버깅 - pinValue 타입: ${typeof pinValue}, 값: ${pinValue}, 길이: ${pinValue?.length}`);
        devLog(`[PIN 다이얼로그] 🔍 디버깅 - pinValue 존재 여부: ${!!pinValue}`);
        devLog(`[PIN 다이얼로그] 🔍 콜백 호출 시점 self.isCallbackResolved: ${self.isCallbackResolved}`);

        // 🔥 새로운 검증 시작인 경우 강제로 상태 초기화
        if (self.newVerificationStarted) {
          devLog("[PIN 다이얼로그] 🔄 새로운 검증 시작으로 인한 강제 상태 초기화");
          self.isCallbackResolved = false;
          self.newVerificationStarted = false;
        }

        // 이미 콜백이 해결된 경우 무시
        if (self.isCallbackResolved) {
          devLog("[PIN 다이얼로그] ⚠️ 콜백이 이미 해결됨, 무시");
          return;
        }

        // 콜백 해결 상태로 변경
        self.isCallbackResolved = true;
        devLog(`[PIN 다이얼로그] 🔍 콜백 해결 상태 변경 후: ${self.isCallbackResolved}`);

        // 🔥 라이프사이클 기반 처리 시작
        devLog("[PIN 다이얼로그] 🔥🔥🔥 라이프사이클 기반 처리 시작 🔥🔥🔥");

        try {
          devLog(`[PIN 검증] 🔍 조건 분기 확인 - success: ${success}, pinValue 존재: ${!!pinValue}`);
          devLog(`[PIN 검증] 🔍 상세 조건 - pinValue: "${pinValue}", 타입: ${typeof pinValue}, 길이: ${pinValue?.length}`);

          if (success) {
            devLog("[PIN 검증] ✅ 조건 1: success=true - 네이티브 PIN 검증 성공");

            // 디바이스 정보 저장
            await self._saveUserDataToDevice(formData);

            // 성공 시 콜백 정리
            self._cleanupCallback();
            onSuccess?.();
          } else if (pinValue && pinValue.toString().length > 0) {
            // 🔥 PIN 값이 있으면 서버 검증 시도 (success 값과 무관)
            devLog("[PIN 검증] ✅ 조건 2: pinValue 존재 - 서버 검증 시도");
            devLog("[PIN 검증] ⭐ PIN 값 존재, 서버 검증 시도");
            devLog("[PIN 검증] 받은 PIN 값:", pinValue);

            onStateChange?.('server_verifying');

            devLog("[PIN 검증] 🔥🔥🔥 verifyPinWithServer 호출 직전 (라이프사이클) 🔥🔥🔥");
            const serverVerificationSuccess = await self.verifyPinWithServer(pinValue, formData);
            devLog("[PIN 검증] 🔥🔥🔥 verifyPinWithServer 호출 완료 (라이프사이클) 🔥🔥🔥, 결과:", serverVerificationSuccess);

            if (serverVerificationSuccess) {
              devLog("[PIN 검증] 서버 PIN 검증 성공 - 로그인 진행");

              // 네이티브에 서버 검증 성공 알림
              self._notifyPinVerificationSuccess(pinValue);

              // 디바이스에 정보 저장
              await self._saveUserDataToDevice(formData, pinValue);

              // 성공 시 콜백 정리
              self._cleanupCallback();
              onSuccess?.();
            } else {
              // 서버 검증 실패
              devLog("[PIN 검증] ⭐ 서버 PIN 검증 실패 (라이프사이클) - _handleVerificationFailure 호출");
              devLog("[PIN 검증] 🔥🔥🔥 _handleVerificationFailure 호출 직전 (라이프사이클) 🔥🔥🔥");
              devLog("[PIN 검증] 🔥 callbacks 객체 확인 (라이프사이클):", callbacks);
              devLog("[PIN 검증] 🔥 callbacks.onStateChange 존재 (라이프사이클):", !!callbacks.onStateChange);

              await self._handleVerificationFailure(callbacks);

              devLog("[PIN 검증] 🔥🔥🔥 _handleVerificationFailure 호출 완료 (라이프사이클) 🔥🔥🔥");
            }
          } else {
            devLog("[PIN 검증] ❌ 조건 3: else - PIN 값 없음 또는 기타");
            devLog(`[PIN 검증] 🔍 else 조건 디버깅 - success: ${success}, pinValue: "${pinValue}", pinValue 타입: ${typeof pinValue}`);
            // 🔥 PIN 값이 없는 경우만 취소로 처리
            devLog("[PIN 검증] ⭐ PIN 값 없음 - 검증 취소로 처리");
            self._handleVerificationCancel(callbacks);
          }
        } catch (error) {
          console.error("[PIN 검증] 라이프사이클 처리 중 오류:", error);
          onError?.(error);
        }

        devLog("[PIN 다이얼로그] 🔥🔥🔥 라이프사이클 기반 처리 완료 🔥🔥🔥");
      };

      devLog(`[PIN 검증] 새로운 콜백 함수 등록 완료: ${callbackName}`);
    } else if (platform === 'ios') {
      if (!window.webkit?.messageHandlers?.BRIDGE) {
        console.error('iOS PIN 확인을 지원하지 않습니다.');
        return;
      }

      // 🔥 iOS 브릿지 초기화 (스위프트 개발자 명세 준수)
      window.iosBridge = window.iosBridge || {};

      devLog(`[PIN 검증] iOS PIN 검증 콜백 설정 시작 - 모드: ${this.currentMode}`);

      // this 참조를 로컬 변수로 저장 (클로저 문제 해결)
      const self = this;
      // 🔥 기존 콜백 함수 완전 삭제 (Android와 동일)
      if (window[FIXED_CALLBACK_NAME]) {
        devLog(`[PIN 검증] 기존 iOS 콜백 함수 삭제: ${FIXED_CALLBACK_NAME}`);
        delete window[FIXED_CALLBACK_NAME];
      }

      // 🔥 iOS용 고정 콜백 등록 (Android와 동일한 구조)
      window[FIXED_CALLBACK_NAME] = async (success, errorMessage, pinValue) => {
        devLog(`[PIN 다이얼로그] iOS 고정 콜백 호출: 성공=${success}, PIN값=${pinValue}`);
        devLog(`[PIN 다이얼로그] 🔍 디버깅 - success 타입: ${typeof success}, 값: ${success}`);
        devLog(`[PIN 다이얼로그] 🔍 디버깅 - pinValue 타입: ${typeof pinValue}, 값: ${pinValue}, 길이: ${pinValue?.length}`);
        devLog(`[PIN 다이얼로그] 🔍 디버깅 - pinValue 존재 여부: ${!!pinValue}`);
        devLog(`[PIN 다이얼로그] 🔍 콜백 호출 시점 self.isCallbackResolved: ${self.isCallbackResolved}`);

        // 🔥 새로운 검증 시작인 경우 강제로 상태 초기화
        if (self.newVerificationStarted) {
          devLog("[PIN 다이얼로그] 🔄 새로운 검증 시작으로 인한 강제 상태 초기화");
          self.isCallbackResolved = false;
          self.newVerificationStarted = false;
        }

        // 이미 콜백이 해결된 경우 무시
        if (self.isCallbackResolved) {
          devLog("[PIN 다이얼로그] ⚠️ iOS 콜백이 이미 해결됨, 무시");
          return;
        }

        // 콜백 해결 상태로 변경
        self.isCallbackResolved = true;
        devLog(`[PIN 다이얼로그] 🔍 콜백 해결 상태 변경 후: ${self.isCallbackResolved}`);

        // 🔥 라이프사이클 기반 처리 시작
        devLog("[PIN 다이얼로그] 🔥🔥🔥 라이프사이클 기반 처리 시작 (iOS) 🔥🔥🔥");

        try {
          devLog(`[PIN 검증] 🔍 조건 분기 확인 - success: ${success}, pinValue 존재: ${!!pinValue}`);
          devLog(`[PIN 검증] 🔍 상세 조건 - pinValue: "${pinValue}", 타입: ${typeof pinValue}, 길이: ${pinValue?.length}`);

          if (success) {
            devLog("[PIN 검증] ✅ 조건 1: success=true - 네이티브 PIN 검증 성공");

            // 디바이스 정보 저장
            await self._saveUserDataToDevice(formData);

            // 성공 시 콜백 정리
            self._cleanupCallback();
            onSuccess?.();
          } else if (pinValue && pinValue.toString().length > 0) {
            // 🔥 PIN 값이 있으면 서버 검증 시도 (success 값과 무관)
            devLog("[PIN 검증] ✅ 조건 2: pinValue 존재 - 서버 검증 시도");
            devLog("[PIN 검증] ⭐ PIN 값 존재, 서버 검증 시도");
            devLog("[PIN 검증] 받은 PIN 값:", pinValue);

            onStateChange?.('server_verifying');

            devLog("[PIN 검증] 🔥🔥🔥 verifyPinWithServer 호출 직전 (iOS 라이프사이클) 🔥🔥🔥");
            const serverVerificationSuccess = await self.verifyPinWithServer(pinValue, formData);
            devLog("[PIN 검증] 🔥🔥🔥 verifyPinWithServer 호출 완료 (iOS 라이프사이클) 🔥🔥🔥, 결과:", serverVerificationSuccess);

            if (serverVerificationSuccess) {
              devLog("[PIN 검증] 서버 PIN 검증 성공 - 로그인 진행");

              // 네이티브에 서버 검증 성공 알림
              self._notifyPinVerificationSuccess(pinValue);

              // 디바이스에 정보 저장
              await self._saveUserDataToDevice(formData, pinValue);

              // 성공 시 콜백 정리
              self._cleanupCallback();
              onSuccess?.();
            } else {
              // 서버 검증 실패
              devLog("[PIN 검증] ⭐ 서버 PIN 검증 실패 (iOS 라이프사이클) - _handleVerificationFailure 호출");
              devLog("[PIN 검증] 🔥🔥🔥 _handleVerificationFailure 호출 직전 (iOS 라이프사이클) 🔥🔥🔥");
              devLog("[PIN 검증] 🔥 callbacks 객체 확인 (iOS 라이프사이클):", callbacks);
              devLog("[PIN 검증] 🔥 callbacks.onStateChange 존재 (iOS 라이프사이클):", !!callbacks.onStateChange);

              await self._handleVerificationFailure(callbacks);

              devLog("[PIN 검증] 🔥🔥🔥 _handleVerificationFailure 호출 완료 (iOS 라이프사이클) 🔥🔥🔥");
            }
          } else {
            devLog("[PIN 검증] ❌ 조건 3: else - PIN 값 없음 또는 기타");
            devLog(`[PIN 검증] 🔍 else 조건 디버깅 - success: ${success}, pinValue: "${pinValue}", pinValue 타입: ${typeof pinValue}`);
            // 🔥 PIN 값이 없는 경우만 취소로 처리
            devLog("[PIN 검증] ⭐ PIN 값 없음 - 검증 취소로 처리");
            self._handleVerificationCancel(callbacks);
          }
        } catch (error) {
          console.error("[PIN 검증] iOS 라이프사이클 처리 중 오류:", error);
          onError?.(error);
        }

        devLog("[PIN 다이얼로그] 🔥🔥🔥 iOS 라이프사이클 기반 처리 완료 🔥🔥��");
      };

      // 🔥 통일된 submitPin 콜백 처리 (auth/main 모드 모두 동일)
      window.iosBridge.submitPin = (pin) => {
        devLog(`[PIN 검증] iOS submitPin 콜백 (${self.currentMode} 모드):`, pin);

        // 이미 콜백이 해결된 경우 무시
        if (self.isCallbackResolved) {
          devLog("[PIN 검증] ⚠️ 콜백이 이미 해결됨 (submitPin), 무시");
          return;
        }

        // 콜백 해결 상태로 변경
        self.isCallbackResolved = true;
        devLog(`[PIN 검증] 콜백 해결 상태 변경: ${self.isCallbackResolved}`);

        // PIN 값이 있으면 서버 검증 시도, 없으면 취소 처리
        if (pin && pin.toString().length > 0) {
          devLog("[PIN 검증] PIN 값 존재 - 서버 검증 시도");

          // PinVerificationManager의 고정 콜백 시스템과 연동
          if (window[FIXED_CALLBACK_NAME]) {
            devLog(`[PIN 검증] 고정 콜백 호출: ${FIXED_CALLBACK_NAME}`);
            window[FIXED_CALLBACK_NAME](false, null, pin); // success=false, PIN값은 전달
          } else {
            console.error(`[PIN 검증] 고정 콜백이 없음: ${FIXED_CALLBACK_NAME}`);
          }
        } else {
          devLog("[PIN 검증] PIN 값 없음 - 취소 처리");

          // 취소 처리
          if (window[FIXED_CALLBACK_NAME]) {
            devLog(`[PIN 검증] 취소 콜백 호출: ${FIXED_CALLBACK_NAME}`);
            window[FIXED_CALLBACK_NAME](false, "PIN 입력 취소", null);
          }
        }
      };

      

      // 🔥 전역에 저장된 PIN 값 확인 (BiometricService 폴백 지원)
      if (window._pendingPinValue && window._pendingPinTimestamp) {
        const pinAge = Date.now() - window._pendingPinTimestamp;
        if (pinAge < 5000) { // 5초 이내의 PIN만 유효
          devLog("[PIN 검증] 전역 저장된 PIN 발견:", window._pendingPinValue);
          const pendingPin = window._pendingPinValue;

          // 전역 PIN 정리
          delete window._pendingPinValue;
          delete window._pendingPinTimestamp;

          // 즉시 처리
          setTimeout(() => {
            if (window.iosBridge.submitPin) {
              window.iosBridge.submitPin(pendingPin);
            }
          }, 50);
        }
      }

      devLog(`[PIN 검증] iOS submitPin 콜백 등록 완료 - 모드: ${this.currentMode}`);
    }
  }

  /**
   * 네이티브 PIN 다이얼로그 호출
   * @private
   */
  _callNativePinDialog() {
    const platform = biometricService.getPlatform();

    if (platform === 'android') {
      // 🔥 모드에 따라 다른 메서드 호출
      if (this.currentMode === 'auth') {
        // AuthenticationFlow용: 기존 verifyPin 메서드 사용
        if (window.BRIDGE?.verifyPin) {
          const callbackName = FIXED_CALLBACK_NAME;
          devLog(`[PIN 검증] AuthenticationFlow용 네이티브 PIN 검증 호출: ${callbackName}`);
          window.BRIDGE.verifyPin(callbackName);
        } else {
          console.error('AuthenticationFlow용 PIN 확인을 지원하지 않습니다.');
          return;
        }
      } else {
        // Main2용: verifyPinForMain 메서드 우선 사용
        if (window.BRIDGE?.verifyPinForMain) {
          const callbackName = FIXED_CALLBACK_NAME;
          devLog(`[PIN 검증] Main2용 네이티브 PIN 검증 호출: ${callbackName}`);
          window.BRIDGE.verifyPinForMain(callbackName);
        } else if (window.BRIDGE?.verifyPin) {
          // 폴백: 기존 메서드 사용
          const callbackName = FIXED_CALLBACK_NAME;
          devLog(`[PIN 검증] 기존 네이티브 PIN 검증 호출 (폴백): ${callbackName}`);
          window.BRIDGE.verifyPin(callbackName);
        } else {
          console.error('PIN 확인을 지원하지 않습니다.');
          return;
        }
      }
    } else if (platform === 'ios') {
      devLog(`[PIN 검증] iOS pinCheck 명령어 호출 - 모드: ${this.currentMode}`);
      devLog("[PIN 검증] 🔥🔥🔥 iOS PIN 다이얼로그 즉시 호출 🔥🔥🔥");
      
      try {
        window.webkit.messageHandlers.BRIDGE.postMessage({
          command: "pinCheck",
          type: this.currentMode
        });
        devLog("[PIN 검증] ✅ iOS pinCheck 메시지 전송 완료");
      } catch (error) {
        console.error("[PIN 검증] ❌ iOS pinCheck 호출 오류:", error);
      }
    }
  }

  /**
   * 검증 실패 처리
   */
  async _handleVerificationFailure(callbacks) {
    devLog("🔥🔥🔥 [PIN 검증] _handleVerificationFailure 함수 호출됨! 🔥🔥🔥");
    devLog("[PIN 검증] callbacks 객체:", callbacks);
    devLog("[PIN 검증] callbacks.onStateChange 존재 여부:", !!callbacks.onStateChange);
    devLog("[PIN 검증] callbacks.onFailure 존재 여부:", !!callbacks.onFailure);

    const { onStateChange, onFailure } = callbacks;

    // 시도 횟수 증가
    const newAttempts = this.verificationAttempts + 1;
    this.verificationAttempts = newAttempts;

    devLog(`[PIN 검증] 서버 PIN 검증 실패 - 시도 횟수: ${newAttempts}/${this.maxAttempts}`);

    if (newAttempts >= this.maxAttempts) {
      devLog("[PIN 검증] 최대 시도 횟수 초과");

      // �� 5회 실패로 인한 프래그먼트 종료
      devLog("[PIN 검증] 🔥 5회 실패로 인한 프래그먼트 종료 시작");
      this._closePinFragment();

      // 콜백 정리
      this._cleanupCallback();

      onFailure?.("PIN 검증 횟수를 초과했습니다. 다시 시도해주세요.", true);
    } else {
      // 실패 메시지 생성
      const failureMessage = `PIN이 일치하지 않습니다 (${newAttempts}/${this.maxAttempts})`;
      devLog(`[PIN 검증] 생성된 실패 메시지: "${failureMessage}"`);

      devLog(`[PIN 검증] 재시도 가능 - 남은 횟수: ${this.maxAttempts - newAttempts}`);

      // 프래그먼트에 실패 상태와 메시지 전달
      devLog(`[PIN 검증] 프래그먼트에 실패 메시지 전달: "${failureMessage}"`);
      this._updatePinFragmentState('failed', failureMessage);

      // 🔥 스마트한 재시도 준비 - 사용자 입력 상태 고려
      this._scheduleSmartRetry(onStateChange);
    }
  }

  /**
   * 🔥 스마트한 재시도 스케줄링 - 사용자 입력 중일 때는 지연
   */
  _scheduleSmartRetry(onStateChange) {
    // 기존 타이머가 있다면 취소
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }

    const attemptRetry = () => {
      // 사용자가 현재 입력 중인지 확인
      const isCurrentlyInputting = this.isUserInputting &&
        this.inputStartTime &&
        (Date.now() - this.inputStartTime < 2000); // 2초 이내 입력 시작

      if (isCurrentlyInputting) {
        devLog("[PIN 검증] 🔄 사용자 입력 중 감지 - 재시도 200ms 지연");
        // 사용자가 입력 중이면 200ms 후 다시 확인 (더 빠르게)
        this.retryTimeoutId = setTimeout(attemptRetry, 200);
        return;
      }

      // 사용자 입력이 끝났거나 시작되지 않았으면 재시도 진행
      devLog("[PIN 검증] 재시도 준비 - ready_for_retry 상태로 변경");
      this.newVerificationStarted = true;

      if (onStateChange) {
        onStateChange('ready_for_retry', '다시 PIN 번호를 입력해주세요');
      }

      devLog("[PIN 검증] 🔥🔥🔥 _handleVerificationFailure 호출 완료 🔥🔥🔥");
    };

    // 초기 지연 시간을 500ms로 단축
    devLog("[PIN 검증] 스마트 재시도 스케줄링 시작 - 500ms 후 확인");
    this.retryTimeoutId = setTimeout(attemptRetry, 500); // 🔥 1000ms → 500ms로 단축 (속도 개선)
  }

  /**
   * 검증 취소 처리
   */
  _handleVerificationCancel(callbacks) {
    devLog("[PIN 검증] PIN 검증 취소 - 시도 횟수:", this.verificationAttempts + 1 + "/" + this.maxAttempts);

    const newAttempts = this.verificationAttempts + 1;
    this.verificationAttempts = newAttempts;

    if (newAttempts >= this.maxAttempts) {
      devLog("[PIN 검증] 최대 시도 횟수 초과로 인한 취소");

      // �� 5회 실패로 인한 프래그먼트 종료
      devLog("[PIN 검증] 🔥 5회 실패로 인한 프래그먼트 종료 시작 (취소)");
      this._closePinFragment();

      // 최대 시도 횟수 초과 시 콜백 정리
      this._cleanupCallback();

      const { onFailure } = callbacks;
      onFailure?.("PIN 검증이 취소되었습니다. 다시 시도해주세요.", true);
    } else {
      devLog("[PIN 검증] 사용자가 PIN 검증 취소");

      // 콜백 정리
      this._cleanupCallback();

      const { onError } = callbacks;
      onError?.(new Error("PIN 검증이 취소되었습니다. 다시 시도해주세요."));
    }
  }

  /**
   * 사용자 데이터를 디바이스에 저장
   */
  async _saveUserDataToDevice(formData, pinNumber = null) {
    try {
      biometricService.savePhoneNumber(formData.phoneNumber);
      devLog("[PIN 검증] 전화번호 디바이스에 저장 완료");

      if (pinNumber) {
        devLog("[PIN 검증] PIN 디바이스에 저장 시도", formData.phoneNumber, pinNumber);
        const result = await biometricService.savePinDirectly(formData.phoneNumber, pinNumber);
        devLog("[PIN 검증] PIN 디바이스에 저장 성공:", result);
      }
    } catch (error) {
      console.error("[PIN 검증] 디바이스 저장 실패:", error);
    }
  }

  /**
   * PIN 검증 성공 알림
   */
  _notifyPinVerificationSuccess(pinNumber) {
    try {
      biometricService.notifyPinVerificationSuccess(pinNumber);
    } catch (error) {
      console.error("[PIN 검증] 성공 알림 오류:", error);
    }
  }

  /**
   * 6번 방식: PIN 프래그먼트 상태 업데이트
   */
  _updatePinFragmentState(state, message = "", additionalInfo = {}) {
    try {
      if (window.BRIDGE?.updatePinFragmentState) {
        devLog(`[PIN 검증] 프래그먼트 상태 업데이트: ${state} - ${message}`);
        devLog(`[PIN 검증] 추가 정보:`, additionalInfo);

        // 🔥 모든 상태에서 기존 2개 파라미터 메서드만 호출
        devLog(`[PIN 검증] 기존 2개 파라미터로 호출: state=${state}, message=${message}`);

        // 🔥 failed 상태일 때 메시지가 비어있지 않은지 확인
        if (state === 'failed' && !message) {
          console.warn(`[PIN 검증] ⚠️ failed 상태인데 메시지가 비어있음!`);
          message = "PIN이 일치하지 않습니다";
        }

        devLog(`[PIN 검증] 🔥 네이티브 브릿지 호출: updatePinFragmentState("${state}", "${message}")`);
        window.BRIDGE.updatePinFragmentState(state, message);
        devLog(`[PIN 검증] 🔥 네이티브 브릿지 호출 완료`);

        // 🔥 reset 상태일 때 명시적으로 PIN 입력 필드 초기화 요청
        if (state === 'reset') {
          devLog(`[PIN 검증] reset 상태 감지 - PIN 입력 필드 초기화 요청`);
          this._clearPinInputField();
        }
      } else {
        console.warn("[PIN 검증] updatePinFragmentState 함수 없음 - 폴백 처리");

        // 폴백: 네이티브 브릿지가 없거나 함수가 없는 경우
        if (state === 'failed') {
          // 실패 시 토스트 메시지로 대체
          if (window.showToast) {
            window.showToast(message || "PIN이 일치하지 않습니다");
          }
        }
      }
    } catch (error) {
      console.error("[PIN 검증] 프래그먼트 상태 업데이트 오류:", error);
    }
  }

  /**
   * PIN 입력 필드 명시적 초기화 요청
   */
  _clearPinInputField() {
    try {
      if (window.BRIDGE?.clearPinInputField) {
        devLog(`[PIN 검증] 네이티브 브릿지 호출: clearPinInputField()`);
        window.BRIDGE.clearPinInputField();
      } else {
        console.warn("[PIN 검증] clearPinInputField 함수 없음 - 폴백 처리");

        // 폴백: 네이티브 브릿지가 없거나 함수가 없는 경우
        if (window.showToast) {
          window.showToast("PIN 입력 필드를 초기화할 수 없습니다");
        }
      }
    } catch (error) {
      console.error("[PIN 검증] 프래그먼트 상태 업데이트 오류:", error);
    }
  }

  /**
   * 시도 횟수 초기화
   */
  resetAttempts() {
    devLog("[PIN 검증] 시도 횟수 초기화");
    this.verificationAttempts = 0;
    this.isVerifying = false;
  }

  /**
   * 현재 검증 중인지 확인
   */
  isCurrentlyVerifying() {
    return this.isVerifying;
  }

  /**
   * 저장된 PIN 정보 확인
   * @private
   * @returns {Promise<boolean>} 저장된 정보 존재 여부
   */
  async _hasStoredPinInfo() {
    try {
      const hasPin = await biometricService.hasPin();
      const phoneNumber = biometricService.getPhoneNumber();
      return hasPin && phoneNumber;
    } catch (error) {
      console.error("[PIN 검증] 저장된 정보 확인 오류:", error);
      return false;
    }
  }

  /**
   * 네이티브 PIN 프래그먼트 상태 초기화
   * @private
   */
  _resetNativePinFragmentState() {
    try {
      devLog("[PIN 검증] 🔄 네이티브 프래그먼트에 상태 초기화 신호 전송");

      // 네이티브에 상태 초기화 신호 전송 (다음 콜백에서 적용됨)
      if (window.BRIDGE && window.BRIDGE.signalPinVerificationStart) {
        window.BRIDGE.signalPinVerificationStart();
        devLog("[PIN 검증] ✅ 네이티브에 새로운 검증 시작 신호 전송 완료");
      } else {
        console.warn("[PIN 검증] ⚠️ signalPinVerificationStart 메서드 없음");
      }
    } catch (error) {
      console.error("[PIN 검증] ❌ 네이티브 상태 초기화 신호 전송 오류:", error);
    }
  }

  /**
   * 🔥 5회 실패로 인한 프래그먼트 종료
   */
  _closePinFragment() {
    devLog("[PIN 검증] 🔥 5회 실패로 인한 프래그먼트 종료 시작");

    try {
      // 🔥 올바른 네이티브 브릿지 경로 사용
      if (window.BRIDGE && window.BRIDGE.notifyServerVerificationSuccess) {
        devLog("[PIN 검증] 🔥 BRIDGE를 통한 프래그먼트 종료 신호 전송");
        window.BRIDGE.notifyServerVerificationSuccess();
        devLog("[PIN 검증] ✅ 프래그먼트 종료 신호 전송 완료");
      } else if (window.Android && window.Android.notifyServerVerificationSuccess) {
        devLog("[PIN 검증] Android 브릿지를 통한 프래그먼트 종료 신호 전송");
        window.Android.notifyServerVerificationSuccess();
      } else if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.notifyServerVerificationSuccess) {
        devLog("[PIN 검증] iOS 브릿지를 통한 프래그먼트 종료 신호 전송");
        window.webkit.messageHandlers.notifyServerVerificationSuccess.postMessage({});
      } else {
        devLog("[PIN 검증] ⚠️ 네이티브 브릿지를 찾을 수 없음");
        devLog("[PIN 검증] 🔍 사용 가능한 브릿지 확인:");
        devLog("[PIN 검증] - window.BRIDGE:", !!window.BRIDGE);
        devLog("[PIN 검증] - window.Android:", !!window.Android);
        devLog("[PIN 검증] - window.webkit:", !!window.webkit);

        // 🔥 폴백: 프래그먼트 상태를 'close'로 업데이트
        devLog("[PIN 검증] 폴백: 프래그먼트 상태를 'close'로 업데이트");
        this._updatePinFragmentState('close', '최대 시도 횟수 초과');
      }

      // 🔥 재인증 상태로 전환 (전역 상태 업데이트)
      devLog("[PIN 검증] 🔥 재인증 상태로 전환 시작");
      if (window.setIsReauthentication) {
        window.setIsReauthentication(true);
        devLog("[PIN 검증] ✅ 재인증 상태 전환 완료");
      } else {
        devLog("[PIN 검증] ⚠️ setIsReauthentication 함수를 찾을 수 없음");
      }

      devLog("[PIN 검증] 5회 실패 처리 완료");
    } catch (error) {
      console.error("[PIN 검증] 프래그먼트 종료 중 오류:", error);
    }
  }

  /**
   * 🔥 사용자 입력 시작 알림 (Android에서 호출)
   */
  notifyUserInputStarted() {
    devLog("[PIN 검증] 🎯 사용자 입력 시작 감지");
    this.isUserInputting = true;
    this.inputStartTime = Date.now();
  }

  /**
   * 🔥 사용자 입력 종료 알림 (Android에서 호출)
   */
  notifyUserInputEnded() {
    devLog("[PIN 검증] 🎯 사용자 입력 종료 감지");
    this.isUserInputting = false;
    this.inputStartTime = null;
  }

  /**
   * 🔥 사용자 입력 상태 초기화
   */
  _resetUserInputState() {
    devLog("[PIN 검증] 🔄 사용자 입력 상태 초기화");
    this.isUserInputting = false;
    this.inputStartTime = null;

    // 재시도 타이머도 정리
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
      this.retryTimeoutId = null;
    }
  }

  /**
   * 🔥 Main 모드 Face ID 처리 (새로운 함수)
   */
  async _handleMainFaceId(formData, callbacks) {
    const { onSuccess, onFailure, onError, onStateChange } = callbacks;
    
    devLog("[PIN 검증] 🔐 Main 모드 Face ID 처리 시작");
    
    // Main Face ID 콜백 등록
    this._setupMainFaceIdCallback(formData, callbacks);
    
    // mainFaceId 호출
    devLog("[PIN 검증] 🔐 mainFaceId 명령어 호출");
    if (window.webkit?.messageHandlers?.BRIDGE) {
      window.webkit.messageHandlers.BRIDGE.postMessage("mainFaceId");
      devLog("[PIN 검증] ✅ mainFaceId 명령어 전송 완료");
    } else {
      console.warn("[PIN 검증] iOS 브릿지 없음 - PIN 다이얼로그로 폴백");
      // iOS 브릿지가 없으면 바로 PIN 다이얼로그로
      this._callNativePinDialog();
      this._setupNativePinDialogCallback(formData, callbacks);
    }
  }

  /**
   * 🔐 Main Face ID 콜백 등록 및 처리 (새로운 함수)
   */
  _setupMainFaceIdCallback(formData, callbacks) {
    const { onSuccess, onFailure, onError, onStateChange } = callbacks;
    
    devLog("[PIN 검증] 🔐 Main Face ID 콜백 등록 시작");
    
    // Main Face ID 콜백 함수 등록
    window.BRIDGE.mainFaceIdCallback = (result) => {
      devLog("[PIN 검증] 🔐 Main Face ID 콜백 받음:", result);
      
      // 🔥 "PIN" 값은 PIN 입력 모드로의 전환을 의미함 (Face ID 성공이 아님)
      if (result === 'PIN') {
        devLog("[PIN 검증] 🔐 네이티브에서 PIN 입력 모드로 전환됨");
        devLog("[PIN 검증] 🔐 PIN 다이얼로그 콜백 등록");
        this._setupNativePinDialogCallback(formData, callbacks);
        return;
      }
      
      if (result === 'cancel') {
        // ❌ Face ID 취소 → 네이티브에서 자동으로 PIN 다이얼로그 호출됨
        devLog("[PIN 검증] 🔐 Main Face ID 취소 - 네이티브에서 PIN 다이얼로그 자동 호출됨");
        devLog("[PIN 검증] 🔐 PIN 다이얼로그 콜백 등록");
        this._setupNativePinDialogCallback(formData, callbacks);
      } else if (result === 'failure') {
        // ❌ Face ID 실패 → PIN 다이얼로그로 폴백
        devLog("[PIN 검증] 🔐 Main Face ID 실패 - PIN 다이얼로그로 폴백");
        this._callNativePinDialog();
        this._setupNativePinDialogCallback(formData, callbacks);
      } else if (result && typeof result === 'string' && result.length > 10) {
        // ✅ Face ID 성공 → 생체인증 서버 검증 (실제 생체인증 데이터인 경우만)
        devLog("[PIN 검증] 🔐 Main Face ID 성공 - 생체인증 서버 검증 시작");
        this._verifyBiometricSignature(result, formData, callbacks);
      } else {
        // ❌ 기타 예상하지 못한 값 → PIN 다이얼로그로 폴백
        devLog("[PIN 검증] 🔐 예상하지 못한 Main Face ID 결과 - PIN 다이얼로그로 폴백:", result);
        this._callNativePinDialog();
        this._setupNativePinDialogCallback(formData, callbacks);
      }
    };
    
    devLog("[PIN 검증] 🔐 Main Face ID 콜백 등록 완료");
  }
}

export default new PinVerificationManager();